import { CartLinesUpdateEvent, StandardEvents } from "@shopify/events";

const backdrop = document.createElement("button");
backdrop.type = "button";
backdrop.className = "dtw-cart-backdrop";
backdrop.setAttribute("aria-label", "Close cart");
backdrop.tabIndex = -1;
backdrop.addEventListener("click", () =>
  document.getElementById("cart-drawer")?.close(),
);
document.body.append(backdrop);
const search = document.getElementById("dtw-search-dialog");
let previousFocus;
const closeSearch = () => search?.close();
const openCart = async () => {
  await customElements.whenDefined("theme-drawer");
  document.getElementById("cart-drawer")?.open();
};
document.addEventListener("click", async (event) => {
  const trigger = event.target.closest(
    "[data-dtw-search-open], [data-dtw-cart-open]",
  );
  if (!trigger) return;
  if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  document.querySelector(".dtw-mobile-menu")?.removeAttribute("open");
  if (trigger.hasAttribute("data-dtw-cart-open")) {
    await openCart();
    return;
  }
  previousFocus = trigger;
  search.showModal();
  document.documentElement.classList.add("dtw-search-is-open");
  search.querySelector("input").focus();
});
for (const name of ["theme-drawer:open", "theme-drawer:close"])
  document.addEventListener(name, (event) => {
    if (event.target.id !== "cart-drawer") return;
    document
      .querySelectorAll("[data-dtw-cart-open]")
      .forEach((trigger) =>
        trigger.setAttribute(
          "aria-expanded",
          String(event.target.hasAttribute("open")),
        ),
      );
  });
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise
    ?.then(({ cart }) => {
      if (cart?.totalQuantity == null) return;
      document
        .querySelectorAll("[data-dtw-cart-count]")
        .forEach((count) => (count.textContent = cart.totalQuantity));
    })
    .catch(() => {});
});

if (search) {
  const input = search.querySelector("input");
  const results = search.querySelector(".dtw-search-results");
  const status = search.querySelector("[role=status]");
  const initialResults = results.innerHTML;
  let timer,
    request,
    sequence = 0;
  search
    .querySelector("[data-dtw-search-close]")
    .addEventListener("click", closeSearch);
  search.addEventListener("click", (event) => {
    if (event.target !== search) return;
    const r = search.getBoundingClientRect();
    if (
      event.clientX < r.left ||
      event.clientX > r.right ||
      event.clientY < r.top ||
      event.clientY > r.bottom
    )
      closeSearch();
  });
  search.addEventListener("close", () => {
    document.documentElement.classList.remove("dtw-search-is-open");
    previousFocus?.focus();
  });
  async function performSearch() {
    clearTimeout(timer);
    request?.abort();
    const token = ++sequence;
    const q = input.value.trim();
    if (!q) {
      results.innerHTML = initialResults;
      status.textContent = "EXPLORE THE LINEUP";
      results.removeAttribute("aria-busy");
      return;
    }
    request = new AbortController();
    results.setAttribute("aria-busy", "true");
    status.textContent = "Searching the lineup…";
    try {
      const params = new URLSearchParams({
        q,
        "resources[type]": "product",
        "resources[limit]": "10",
        "resources[options][unavailable_products]": "show",
      });
      const response = await fetch(
        `${search.dataset.root}search/suggest.json?${params}`,
        { signal: request.signal },
      );
      if (!response.ok) throw new Error("Search unavailable");
      const data = await response.json();
      if (token !== sequence) return;
      const products = data.resources.results.products;
      results.replaceChildren();
      status.textContent = products.length
        ? `${products.length} RESULT${products.length === 1 ? "" : "S"} FOR “${q}”`
        : `No matches for “${q}”. Try “tee” or “fleece”.`;
      for (const product of products) {
        const link = document.createElement("a");
        link.className = "dtw-search-result";
        const url = new URL(product.url, location.origin);
        if (url.origin !== location.origin) continue;
        link.href = url.href;
        if (product.image) {
          const image = document.createElement("img");
          image.src = product.image;
          image.alt = product.title;
          image.width = 80;
          image.height = 80;
          image.loading = "lazy";
          link.append(image);
        }
        const content = document.createElement("span"),
          title = document.createElement("strong"),
          price = document.createElement("small"),
          arrow = document.createElement("b");
        title.textContent = product.title;
        price.textContent = `From ${new Intl.NumberFormat(document.documentElement.lang, { style: "currency", currency: search.dataset.currency }).format(Number(product.price))}`;
        arrow.textContent = "↗";
        arrow.setAttribute("aria-hidden", "true");
        content.append(title, price);
        link.append(content, arrow);
        results.append(link);
      }
    } catch (error) {
      if (error.name === "AbortError" || token !== sequence) return;
      results.replaceChildren();
      status.textContent =
        "Search is temporarily unavailable. Please try again.";
    } finally {
      if (token === sequence) results.removeAttribute("aria-busy");
    }
  }
  search.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      closeSearch();
    }
  });
  input.addEventListener("input", () => {
    request?.abort();
    sequence++;
    clearTimeout(timer);
    timer = setTimeout(performSearch, 180);
  });
  search.querySelector("form").addEventListener("submit", (event) => {
    event.preventDefault();
    performSearch();
  });
  search.querySelectorAll("[data-dtw-query]").forEach((button) =>
    button.addEventListener("click", () => {
      input.value = button.dataset.dtwQuery;
      performSearch();
      input.focus();
    }),
  );
}

document.addEventListener("submit", async (event) => {
  const form = event.target.closest("[data-dtw-package-form]");
  if (!form || !document.getElementById("cart-drawer")) return;
  event.preventDefault();
  if (form.dataset.adding === "true") return;
  const button = form.querySelector("[type=submit]"),
    error = form.querySelector("[data-dtw-cart-error]");
  if (button.disabled) return;
  form.dataset.adding = "true";
  button.disabled = true;
  button.textContent = "ADDING…";
  error.hidden = true;
  const body = new FormData(form);
  body.set("sections", "cart-drawer-section");
  body.set("sections_url", location.pathname);
  const deferred = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: "add",
      context: "product",
      lines: [
        {
          merchandiseId: String(body.get("id")),
          quantity: Number(body.get("quantity")),
        },
      ],
      promise: deferred.promise,
    }),
  );
  try {
    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: "POST",
      body,
      headers: { Accept: "application/json" },
    });
    const added = await response.json();
    if (!response.ok)
      throw new Error(
        added.description || "Unable to add this package. Please try again.",
      );
    const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
    if (!cartResponse.ok)
      throw new Error("Package added. Open your cart to review it.");
    const cart = await cartResponse.json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: "dtw-package-form",
        sections: added.sections,
        didError: false,
      },
    });
    await openCart();
  } catch (problem) {
    deferred.resolve({ detail: { didError: true } });
    error.textContent = problem.message;
    error.hidden = false;
  } finally {
    delete form.dataset.adding;
    button.disabled =
      form.querySelector("[name=id]:checked")?.dataset.available !== "true";
    button.textContent = button.disabled
      ? "SAMPLE PACKAGE — COMING SOON"
      : "ADD PACKAGE TO CART";
  }
});
